/**
 * Overlay Anchor Mode — Split-screen image ↔ Map anchor placement
 *
 * Allows users to geo-register overlay images by placing corresponding
 * anchor pairs on both the image and the map.
 *
 * Anchor flow:
 *   1. Click on image → red crosshair at image pixel coordinates
 *   2. Click on map → green marker at map coordinates
 *   3. AnchorPair created → transform computed, overlay bounds updated live
 *   4. After ≥2 anchors, overlay repositions on the map in real-time
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import { useUIStore } from '../../stores/uiStore';
import { solveTransform, transformPoint } from '../../domain/layout/transform';
import { resolveSignedUrl } from '../../services/signedAssetUrl';
import type { AnchorPair, LayoutTransform } from '../../domain/layout/types';
import '../layout/LayoutAnchorMode.css';

export default function OverlayAnchorMode() {
  const overlayAnchorMode = useUIStore((s) => s.overlayAnchorMode);
  const setOverlayAnchorMode = useUIStore((s) => s.setOverlayAnchorMode);
  const addNotification = useUIStore((s) => s.addNotification);
  const overlays = useProjectStore((s) => s.data.overlays);
  const updateOverlay = useProjectStore((s) => s.updateOverlay);

  // Find the active overlay
  const overlay = useMemo(
    () => overlays.find((o) => o.id === overlayAnchorMode),
    [overlays, overlayAnchorMode],
  );

  // Image viewer state
  const canvasRef = useRef<HTMLDivElement>(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  // Image natural dimensions (original full-res) and display state
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const [displayScale, setDisplayScale] = useState(1); // display pixels / original pixels

  // Anchor placement state (local only, not persisted)
  const [anchors, setAnchors] = useState<AnchorPair[]>([]);
  const [pendingImgPoint, setPendingImgPoint] = useState<{ x: number; y: number } | null>(null);
  const [waitingForMapClick, setWaitingForMapClick] = useState(false);
  const [transform, setTransform] = useState<LayoutTransform | null>(null);

  // ── Load image + adaptive downscale based on GPU resources ──
  // Probes WebGL max texture size to decide the optimal display resolution.
  // Powerful GPUs get full quality; weaker ones get downscaled to fit.

  useEffect(() => {
    if (!overlay) return;
    let revoked = false;

    // Probe GPU max texture size — tells us what the hardware can handle
    const probeCanvas = document.createElement('canvas');
    const gl = probeCanvas.getContext('webgl') || probeCanvas.getContext('experimental-webgl');
    let gpuMaxTexture = 4096; // safe fallback
    if (gl) {
      gpuMaxTexture = (gl as WebGLRenderingContext).getParameter(
        (gl as WebGLRenderingContext).MAX_TEXTURE_SIZE,
      );
      const ext = (gl as WebGLRenderingContext).getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext(); // release probe context
    }
    probeCanvas.remove();
    // Leave 40% headroom for Mapbox (which needs its own textures in the split view)
    const budgetSize = Math.round(gpuMaxTexture * 0.6);
    const MAX_DIM = Math.max(2000, Math.min(budgetSize, 8192));
    console.log(`[AnchorMode] GPU max texture: ${gpuMaxTexture}, display cap: ${MAX_DIM}px`);

    // Pre-sign the asset URL: Image() with crossOrigin='anonymous' strips
    // the sl_refresh cookie, so a raw /api/v2/assets/ URL would 401. crossOrigin
    // is required because the downscale path draws into a canvas + toBlob, which
    // throws SecurityError on a tainted canvas. Same pattern as MapCanvas.
    (async () => {
      const signedUrl = await resolveSignedUrl(overlay.url);
      if (revoked) return;

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (revoked) return;
        const natW = img.naturalWidth;
        const natH = img.naturalHeight;
        setImageSize({ width: natW, height: natH });

        if (natW <= MAX_DIM && natH <= MAX_DIM) {
          setDisplayUrl(signedUrl);
          setDisplayScale(1);
          return;
        }
        const scale = Math.min(MAX_DIM / natW, MAX_DIM / natH);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(natW * scale);
        canvas.height = Math.round(natH * scale);
        canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
        console.log(`[AnchorMode] Downscaling ${natW}×${natH} → ${canvas.width}×${canvas.height} (${Math.round(scale * 100)}%)`);
        canvas.toBlob((blob) => {
          if (revoked || !blob) return;
          setDisplayUrl(URL.createObjectURL(blob));
          setDisplayScale(scale);
        }, 'image/png');
      };
      img.src = signedUrl;
    })();
    return () => {
      revoked = true;
      // Revoke previous blob URL if it was one
      setDisplayUrl((prev) => { if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev); return null; });
    };
  }, [overlay?.url]);

  // ── Fit image on mount (uses display dimensions, not original) ──

  useEffect(() => {
    if (!canvasRef.current || !imageSize || !displayUrl) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const dispW = Math.round(imageSize.width * displayScale);
    const dispH = Math.round(imageSize.height * displayScale);
    const scaleX = rect.width / dispW;
    const scaleY = rect.height / dispH;
    const fitZoom = Math.min(scaleX, scaleY) * 0.95;
    setZoom(fitZoom);
    setPan({
      x: (rect.width - dispW * fitZoom) / 2,
      y: (rect.height - dispH * fitZoom) / 2,
    });
  }, [imageSize, displayUrl, displayScale]);

  // ── Register map click handler ──

  useEffect(() => {
    if (!waitingForMapClick) return;

    const handleMapClick = (e: CustomEvent<{ lng: number; lat: number }>) => {
      if (!pendingImgPoint || !imageSize) return;

      const newAnchor: AnchorPair = {
        id: `anchor-${Date.now()}`,
        pdfX: pendingImgPoint.x, // image pixel X (top-left origin)
        pdfY: pendingImgPoint.y, // image pixel Y (top-left origin)
        mapLng: e.detail.lng,
        mapLat: e.detail.lat,
      };

      const updatedAnchors = [...anchors, newAnchor];
      setAnchors(updatedAnchors);

      // Compute transform and update overlay bounds
      const newTransform = solveTransform(updatedAnchors);
      setTransform(newTransform);

      if (newTransform && overlay) {
        const bounds = computeOverlayBounds(newTransform, imageSize.width, imageSize.height);
        updateOverlay(overlay.id, { bounds });
      }

      setPendingImgPoint(null);
      setWaitingForMapClick(false);

      addNotification({
        type: 'success',
        title: `Anchor ${updatedAnchors.length} placed`,
        message: newTransform
          ? `RMSE: ${newTransform.residualMeters.toFixed(1)}m`
          : 'Need at least 2 anchors for registration',
        duration: 3000,
      });
    };

    window.addEventListener('layout-anchor-map-click', handleMapClick as EventListener);
    return () => window.removeEventListener('layout-anchor-map-click', handleMapClick as EventListener);
  }, [waitingForMapClick, pendingImgPoint, anchors, imageSize, overlay, updateOverlay, addNotification]);

  // ── Image click → anchor placement ──

  const handleImageClick = useCallback((e: React.MouseEvent) => {
    if (isDragging || !canvasRef.current || !imageSize) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Convert screen coords to original image pixel coords
    // Display is downscaled by displayScale, so divide by it to get original pixels
    const imgX = (clickX - pan.x) / zoom / displayScale;
    const imgY = (clickY - pan.y) / zoom / displayScale;

    setPendingImgPoint({ x: imgX, y: imgY });
    setWaitingForMapClick(true);
  }, [isDragging, pan, zoom, imageSize, displayScale]);

  // ── Pan/zoom handlers ──

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    dragStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    setIsDragging(false);

    const handleMove = (me: MouseEvent) => {
      const dx = me.clientX - dragStartRef.current.x;
      const dy = me.clientY - dragStartRef.current.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) setIsDragging(true);
      setPan({
        x: dragStartRef.current.panX + dx,
        y: dragStartRef.current.panY + dy,
      });
    };

    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      setTimeout(() => setIsDragging(false), 10);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  }, [pan]);

  // Use native wheel listener with { passive: false } so preventDefault() works.
  // React's onWheel is passive by default in Chrome, causing console error floods.
  const wheelStateRef = useRef({ zoom, pan });
  wheelStateRef.current = { zoom, pan };

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const { zoom: z, pan: p } = wheelStateRef.current;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      const newZoom = Math.min(Math.max(z * factor, 0.1), 10);

      setPan({
        x: mouseX - (mouseX - p.x) * (newZoom / z),
        y: mouseY - (mouseY - p.y) * (newZoom / z),
      });
      setZoom(newZoom);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // ── Delete anchor ──

  const handleDeleteAnchor = useCallback((id: string) => {
    const updated = anchors.filter((a) => a.id !== id);
    setAnchors(updated);

    const newTransform = updated.length >= 2 ? solveTransform(updated) : null;
    setTransform(newTransform);

    if (newTransform && overlay && imageSize) {
      const bounds = computeOverlayBounds(newTransform, imageSize.width, imageSize.height);
      updateOverlay(overlay.id, { bounds });
    }
  }, [anchors, overlay, imageSize, updateOverlay]);

  // ── Done ──

  const handleDone = useCallback(() => {
    setOverlayAnchorMode(null);
  }, [setOverlayAnchorMode]);

  // ── Convert anchor image coords to screen coords ──

  const anchorScreenPositions = useMemo(() => {
    if (!imageSize) return [];
    return anchors.map((a, i) => ({
      id: a.id,
      num: i + 1,
      screenX: a.pdfX * displayScale * zoom + pan.x,
      screenY: a.pdfY * displayScale * zoom + pan.y,
      residual: transform?.anchorResiduals.find((r) => r.anchorId === a.id),
    }));
  }, [anchors, imageSize, zoom, pan, transform]);

  // ── Pending point screen position ──

  const pendingScreen = useMemo(() => {
    if (!pendingImgPoint || !imageSize) return null;
    return {
      x: pendingImgPoint.x * displayScale * zoom + pan.x,
      y: pendingImgPoint.y * displayScale * zoom + pan.y,
    };
  }, [pendingImgPoint, imageSize, zoom, pan]);

  if (!overlay || !overlayAnchorMode) return null;

  return (
    <div className="anchor-mode">
      {/* Control bar */}
      <div className="anchor-mode__bar">
        <div className="anchor-mode__instructions">
          {waitingForMapClick
            ? 'Now click the same point on the map \u2192'
            : 'Click a recognizable feature on the image \u2190'}
        </div>
        <div className="anchor-mode__info">
          <span className="anchor-mode__count">{anchors.length} anchors</span>
          {transform && (
            <span className={`anchor-mode__rmse anchor-mode__rmse--${
              transform.residualMeters < 5 ? 'good'
                : transform.residualMeters < 20 ? 'fair'
                : 'poor'
            }`}>
              RMSE: {transform.residualMeters.toFixed(1)}m
            </span>
          )}
        </div>
        <button className="anchor-mode__done" onClick={handleDone} type="button">
          Done
        </button>
      </div>

      {/* Image viewer */}
      <div
        ref={canvasRef}
        className="anchor-mode__pdf"
        onMouseDown={handleMouseDown}
        onClick={handleImageClick}
      >
        <div
          className="anchor-mode__pdf-content"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
          }}
        >
          {overlay && displayUrl && (
            <img
              src={displayUrl}
              alt={overlay.name}
              draggable={false}
              style={{ display: 'block' }}
            />
          )}
        </div>

        {/* Anchor markers on image */}
        {anchorScreenPositions.map((a) => (
          <div
            key={a.id}
            className="anchor-mode__marker anchor-mode__marker--placed"
            style={{ left: a.screenX, top: a.screenY }}
            title={`Anchor ${a.num}${a.residual ? ` (${a.residual.errorMeters.toFixed(1)}m)` : ''}`}
          >
            <span>{a.num}</span>
            <button
              className="anchor-mode__marker-delete"
              onClick={(e) => { e.stopPropagation(); handleDeleteAnchor(a.id); }}
              type="button"
              title="Remove anchor"
            >
              ×
            </button>
          </div>
        ))}

        {/* Pending crosshair */}
        {pendingScreen && (
          <div
            className="anchor-mode__marker anchor-mode__marker--pending"
            style={{ left: pendingScreen.x, top: pendingScreen.y }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24">
              <line x1="12" y1="0" x2="12" y2="24" stroke="#ef4444" strokeWidth="2" />
              <line x1="0" y1="12" x2="24" y2="12" stroke="#ef4444" strokeWidth="2" />
              <circle cx="12" cy="12" r="6" fill="none" stroke="#ef4444" strokeWidth="2" />
            </svg>
          </div>
        )}

        {/* Map-click prompt overlay */}
        {waitingForMapClick && (
          <div className="anchor-mode__prompt">
            Click the corresponding point on the map →
          </div>
        )}
      </div>

      {/* Anchor list */}
      <div className="anchor-mode__list">
        <div className="anchor-mode__list-header">Anchors</div>
        {anchors.length === 0 && (
          <div className="anchor-mode__list-empty">
            No anchors yet. Click points on both the image and map to create anchor pairs.
          </div>
        )}
        {anchors.map((a, i) => {
          const residual = transform?.anchorResiduals.find((r) => r.anchorId === a.id);
          return (
            <div key={a.id} className="anchor-mode__list-item">
              <span className="anchor-mode__list-num">{i + 1}</span>
              <span className="anchor-mode__list-coords">
                Img: ({a.pdfX.toFixed(0)}, {a.pdfY.toFixed(0)})
              </span>
              <span className="anchor-mode__list-coords">
                Map: ({a.mapLng.toFixed(5)}, {a.mapLat.toFixed(5)})
              </span>
              {residual && (
                <span className={`anchor-mode__list-error anchor-mode__list-error--${
                  residual.errorMeters < 5 ? 'good' : residual.errorMeters < 20 ? 'fair' : 'poor'
                }`}>
                  {residual.errorMeters.toFixed(1)}m
                </span>
              )}
              <button
                className="anchor-mode__list-delete"
                onClick={() => handleDeleteAnchor(a.id)}
                type="button"
                title="Remove"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Compute overlay 4-corner bounds from a transform and image dimensions.
 * Image coordinate system: origin top-left, X right, Y down.
 * Returns [TL, TR, BR, BL] as [lng, lat] pairs.
 */
function computeOverlayBounds(
  t: LayoutTransform,
  imgWidth: number,
  imgHeight: number,
): [[number, number], [number, number], [number, number], [number, number]] {
  const tl = transformPoint(t, 0, 0);
  const tr = transformPoint(t, imgWidth, 0);
  const br = transformPoint(t, imgWidth, imgHeight);
  const bl = transformPoint(t, 0, imgHeight);
  return [tl, tr, br, bl];
}
